<template>
    <div id="widget" class="widget px-1 px-md-3 py-1 py-md-2 mw-100 position-relative">
        <div class="d-flex justify-content-center align-items-center mb-3">
            <span class="mr-2"><em>Chart range: </em></span
            ><v-date-picker popover-align="right" mode="range" v-model="interval"></v-date-picker>
            <button
                :disabled="['preference_assessment'].includes(goal.tracker_type)"
                class="btn btn-sm btn-outline-primary ml-3"
                @click="chartScaleToFit"
            >
                Scale to fit
            </button>
        </div>

        <div
            v-show="
                ['line', 'point'].includes(chartMetaData.chart_type) &&
                    chartTreatedData.datasets.length
            "
            style="min-height:400px;"
        >
            <line-chart ref="linechart" :chart-data="chartTreatedData" :options="chartOptions">
            </line-chart>
        </div>
        <div
            v-show="chartMetaData.chart_type === 'bar' && chartTreatedData.datasets.length"
            style="min-height:400px;"
        >
            <bar-chart ref="barchart" :chart-data="chartTreatedData" :options="chartOptions">
            </bar-chart>
        </div>

        <transition name="fade">
            <div
                v-if="
                    loading && (overlayStyle.hasOwnProperty('width') || !chartMetaData.chart_type)
                "
                class="overlay w-100 py-4 d-flex justify-content-center align-items-center position-absolute"
                :style="overlayStyle"
            >
                <i class="fa fa-2x fa-spin fa-circle-o-notch text-light"></i>
            </div>
        </transition>
    </div>
</template>

<script>
import LineChart from "./LineChart";
import BarChart from "./BarChart";
export default {
    name: "GoalsChart",
    props: ["goal", "trackers", "customization"],
    components: {
        LineChart,
        BarChart,
    },
    data() {
        return {
            interval: {
                start: new Date(),
                end: new Date(),
            },
            chartMetaData: {},
            chartTreatedData: {
                labels: [],
                datasets: [],
            },
            overlayStyle: {
                background: "rgba(255,255,255,0.7)",
            },
            loading: false,
            chartOptions: {},
        };
    },
    created() {
        this.interval.start.setDate(this.interval.start.getDate() - 7);
        this.$bus.$on("redraw-chart", newAxesLabels => {
            if (newAxesLabels) {
                this.redrawChart(newAxesLabels);
            } else {
                setTimeout(this.redrawChart, 0);
            }
        });
    },
    computed: {
        hasPhaseChangeLine() {
            return this.chartTreatedData.labels.filter(label => label.toString().length <= 3); //'Baseline' or dates are longer than 3 chars.
        },
        phaseChangeIndex() {
            let indexes = [];
            this.chartTreatedData.labels.forEach((label, index) =>
                label.toString().length <= 3 ? indexes.push(index) : null
            );
            return indexes;
        },
    },
    watch: {
        // We need new chart data if we have a new time frame or if we added new tracker data
        interval() {
            this.fetchChartData();
        },
        goal() {
            //this.fetchChartData();
            // When goal changes, trackers change as well.
        },
        trackers() {
            this.fetchChartData();
        },
    },
    methods: {
        chartScaleToFit() {
            if (this.goal.tracker_type === "cold_probe") {
                this.interval = {
                    start: new Date(this.goal.created_at.replace(" ", "T")), // Trackers are in reverse order
                    end: new Date(),
                };
            } else {
                let trackers = [...this.trackers].map(tracker => tracker.session_at);
                if (trackers.length) {
                    this.interval = {
                        start: new Date(trackers[trackers.length - 1].replace(" ", "T")), // Trackers are in reverse order
                        end: new Date(trackers[0].replace(" ", "T")), // TODO: if we implement pagination in goal details, this will need to be updated.
                    };
                }
            }
        },

        fetchChartData() {
            if (!this.interval) return;
            this.showLoadingOverlay(true, "widget");
            axios
                .get(
                    `/api/patients/${this.goal.patient.id}/goals/${
                        this.goal.id
                    }/chart?date_range_min=${this.moment(this.interval.start).format(
                        "YYYY-MM-DD"
                    )}&date_range_max=${this.moment(this.interval.end).format("YYYY-MM-DD")}`
                )
                .then(r => {
                    this.chartMetaData = r.data.meta;
                    this.$bus.$emit("chart-metadata", r.data.meta);
                    this.treatData(r.data.data);
                    this.setChartOptions(r.data.data);
                    this.showLoadingOverlay(false);
                })
                .catch(e => {
                    console.error(e);
                    this.showLoadingOverlay(false);
                });
        },

        redrawChart(newAxesLabels) {
            if (newAxesLabels) {
                this.chartOptions.scales.xAxes[0].scaleLabel.labelString =
                    newAxesLabels.chart_x_axis_label;
                this.chartOptions.scales.yAxes[0].scaleLabel.labelString =
                    newAxesLabels.chart_y1_axis_label;
                if (this.chartOptions.scales.yAxes[1]) {
                    this.chartOptions.scales.yAxes[1].scaleLabel.labelString =
                        newAxesLabels.chart_y2_axis_label;
                }
            }
            this.$refs.linechart.renderChart(this.chartTreatedData, this.chartOptions);
            this.$refs.barchart.renderChart(this.chartTreatedData, this.chartOptions);
        },

        showLoadingOverlay(show, idToCover = null) {
            if (show) {
                let element = document.getElementById(idToCover);
                if (element) {
                    this.overlayStyle.height = element.clientHeight + "px";
                    this.overlayStyle.width = element.clientWidth + "px";
                    this.overlayStyle.zIndex = 2;
                    this.overlayStyle.top = 0;
                    this.overlayStyle.left = 0;
                    this.overlayStyle.background = "rgba(255,255,255,0.7)";
                }
            }
            this.loading = show;
        },

        setChartOptions(data) {
            this.chartOptions = {
                legend: {
                    display: true,
                    labels: {
                        usePointStyle: true,
                    },
                },
                position: "top",
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    xAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: this.chartMetaData.x_axis_label.replace("_", " "),
                            },
                        },
                    ],
                    yAxes: [
                        {
                            id: "y-axis-0",
                            display: true,
                            ticks: {
                                beginAtZero: true,
                                steps: 10,
                            },
                            scaleLabel: {
                                display: true,
                                labelString: this.chartMetaData.y1_axis_label.replace("_", " "),
                            },
                        },
                    ],
                },
            };

            switch (this.goal.tracker_type) {
                case "percent":
                case "articulation":
                    this.chartOptions.scales.yAxes[0].ticks.max = 100; // Percent scale
                    this.chartOptions.lineBeforeIndex = [1, ...this.phaseChangeIndex]; // Draws baseline line, draws phase change lines
                    break;
                case "duration":
                    this.chartOptions.scales.yAxes.push({
                        // Secondary y-axis
                        id: "y-axis-1",
                        gridLines: {
                            borderDash: [8, 4],
                        },
                        display: true,
                        position: "right",
                        scaleLabel: {
                            display: true,
                            labelString: this.chartMetaData.y2_axis_label.replace("_", " "),
                        },
                        ticks: {
                            beginAtZero: true,
                            stepSize: 1,
                        },
                    });
                    this.chartOptions.lineBeforeIndex = [1, ...this.phaseChangeIndex]; // Draws baseline line, draws phase change lines
                    break;
                case "preference_assessment":
                    if (["paired, msw"].includes(this.goal.tracker_type)) {
                        this.chartOptions.scales.yAxes[0].ticks.max = 100; // Percent scale
                    }
                    break;
                case "cme":
                    this.chartOptions.lineBeforeIndex = [1, ...this.phaseChangeIndex]; // Draws baseline line, draws phase change lines
                    this.chartOptions.scales.yAxes[0].ticks.suggestedMax = data.y1_goal_value * 1.1; // Goal-max scale
                    break;
                case "mand":
                case "mand_echoic":
                case "multi_tally":
                    this.chartOptions.lineBeforeIndex = [1, ...this.phaseChangeIndex]; // Draws baseline line, draws phase change lines
                    this.chartOptions.legend.labels.filter = (item, chart) => {
                        return !item.text.includes("aseline");
                    };
                    break;
                case "cold_probe":
                    this.chartOptions.lineBeforeIndex = [...this.phaseChangeIndex]; // Draws phase change lines
                    this.chartOptions.scales.yAxes[0].ticks.suggestedMax = data.y1_goal_value * 1.1; // Goal-max scale
                    break;
                default:
                    this.chartOptions.lineBeforeIndex = [1, ...this.phaseChangeIndex]; // Draws baseline line, draws phase change lines
                    this.chartOptions.scales.yAxes[0].ticks.suggestedMax = data.y1_goal_value * 1.1; // Goal-max scale
            }
        },

        treatData(data) {
            if (!data.x_values.length) return; // No data to plot

            let x = [];
            let y = [];
            let y2 = [];
            let baseline = [];
            let baseline2 = [];
            let target = [];

            // Format x-axis as date for all goal types except preference assessment
            if (["preference_assessment"].includes(this.goal.tracker_type)) {
                x = data.x_values;
            } else {
                x = data.x_values.map(x => {
                    if (x.length > 3 && this.moment(x, "YYYY-MM-DD")._isValid) {
                        return this.moment(x).format("D MMM");
                    }
                    return x;
                });
            }

            // Treats goals with multiple sequences differently (mand, duration)
            if (["mand", "mand_echoic", "multi_tally"].includes(this.goal.tracker_type)) {
                y = Object.values(data.y1_values_array);
                baseline = data.y1_baseline_value_array;
            } else if (["duration"].includes(this.goal.tracker_type)) {
                y = data.y1_values_array;
                //y2 = data.y2_values_array;
                y2 = JSON.parse(JSON.stringify(data.y2_values_array));
                baseline = data.y1_baseline_value_array;
                baseline2 = data.y2_baseline_value_array;
            } else {
                //y = data.y1_values_array[0];
                y = data.y1_values_array;
                baseline = data.y1_baseline_value_array;
            }

            // TODO: Make conversion to seconds in DurationChart.php
            if (this.goal.tracker_type === "duration") {
                target = data.x_values.map(() => data.y1_goal_value / 60);
            } else {
                target = data.x_values.map(() => data.y1_goal_value);
            }

            switch (this.chartMetaData.chart_type) {
                case "line":
                case "point": //Point is a particular case of "line"
                    this.populateLineChart(x, y, baseline, target, y2, baseline2);
                    break;
                case "bar":
                    this.populateBarChart(x, y);
                    break;
            }
        },

        populateLineChart(x, y, baseline, target, y2, baseline2) {
            this.chartTreatedData = {
                labels: x,
                datasets: [
                    {
                        yAxisID: "y-axis-0",
                        label: "Goal",
                        pointRadius: 0,
                        pointBorderWidth: 0,
                        pointStyle: "line",
                        backgroundColor: "transparent",
                        borderColor: "#000000",
                        borderDash: [10, 5],
                        borderWidth: 1,
                        data: target,
                    },
                ],
            };

            switch (this.goal.tracker_type) {
                case "mand":
                case "mand_echoic":
                    this.generateMandSequences(y, baseline);
                    break;
                case "multi_tally":
                case "multi_percent":
                    this.generateMultiTargetSequences(y, baseline);
                    break;
                case "duration":
                    this.generateDurationSequences(y, y2, baseline, baseline2);
                    break;
                default:
                    this.generateDefaultSequence(y, baseline);
            }

            this.generateBaselineSequence(baseline);

            this.formatPointTypeChart();
        },

        formatPointTypeChart() {
            if (this.chartMetaData.chart_type === "point") {
                this.chartTreatedData.datasets.forEach(d => {
                    d.borderColor = "transparent";
                    if (d.label === this.goal.title) {
                        d.pointBorderColor = this.goal.domain.color;
                        d.pointBackgroundColor = this.goal.domain.color;
                    }
                });
            }
        },

        generateBaselineSequence(baseline) {
            if (this.goal.tracker_type)
                if ((baseline && !Array.isArray(baseline)) || (baseline && baseline.length)) {
                    // Add a null first point for every non-baseline dataset
                    this.chartTreatedData.datasets.forEach(d => {
                        if (!d.label.includes("aseline") && d.data) {
                            d.data.unshift(null);
                        }
                    });
                    this.chartTreatedData.labels.unshift("Baseline");
                }
        },

        generateDefaultSequence(y, baseline) {
            y.forEach(dataset => {
                if (this.goal && this.goal.title && this.goal.domain) {
                    this.chartTreatedData.datasets.push({
                        label: this.goal.title,
                        yAxisID: "y-axis-0",
                        pointBorderWidth: 0,
                        pointStyle: "rect",
                        pointBorderColor: "#D43672",
                        spanGaps: true,
                        pointBackgroundColor: "#D43672",
                        backgroundColor: "transparent",
                        borderColor: this.goal.domain.color,
                        data: dataset,
                    });
                }
            });
            if ((baseline && !Array.isArray(baseline)) || (baseline && baseline.length)) {
                this.chartTreatedData.datasets.push({
                    yAxisID: "y-axis-0",
                    label: "Baseline",
                    pointRadius: 5,
                    pointBorderWidth: 2,
                    pointStyle: "circle",
                    backgroundColor: "transparent",
                    borderColor: "#8f8f8f",
                    data: baseline,
                });
            }
        },

        generateMandSequences(y, baseline) {
            let colors = [
                "#77BF34",
                "#EA6A76",
                "#F9AE1F",
                "#b550e7",
                "#4392d4",
                "#7471d5",
                "#F33BFD",
                "#62E5F3",
            ];
            y[0].forEach(allResultsPhase => {
                if (this.goal && this.goal.title && this.goal.domain) {
                    this.chartTreatedData.datasets.push({
                        yAxisID: "y-axis-0",
                        label: this.goal.title,
                        pointBorderWidth: 0,
                        pointStyle: "rect",
                        spanGaps: true,
                        pointBorderColor: "#D43672",
                        pointBackgroundColor: "#D43672",
                        backgroundColor: "transparent",
                        borderColor: this.goal.domain.color,
                        data: allResultsPhase,
                    });
                }
            });
            this.chartTreatedData.datasets.push({
                yAxisID: "y-axis-0",
                label: "Baseline",
                pointRadius: 5,
                pointBorderWidth: 2,
                pointStyle: "circle",
                backgroundColor: "transparent",
                borderColor: "#8f8f8f",
                data: [baseline[0]],
            });
            this.goal.parameters.parameters.forEach((p, i) => {
                y[i + 1].forEach(parameterPhase => {
                    this.chartTreatedData.datasets.push({
                        yAxisID: "y-axis-0",
                        label: p,
                        pointBorderWidth: 0,
                        pointStyle: "cross",
                        spanGaps: true,
                        pointBorderColor: colors[i],
                        pointBackgroundColor: "transparent",
                        backgroundColor: "transparent",
                        borderColor: colors[i],
                        borderWidth: 1,
                        data: parameterPhase,
                    });
                });
                this.chartTreatedData.datasets.push({
                    yAxisID: "y-axis-0",
                    label: `Baseline ${p}`,
                    pointBorderWidth: 0,
                    pointStyle: "cross",
                    pointBorderColor: colors[i],
                    pointBackgroundColor: "transparent",
                    backgroundColor: "transparent",
                    borderColor: colors[i],
                    borderWidth: 1,
                    data: [baseline[i + 1]],
                });
            });
        },

        generateMultiTargetSequences(y, baseline) {
            let colors = [
                "#77BF34",
                "#EA6A76",
                "#F9AE1F",
                "#b550e7",
                "#4392d4",
                "#7471d5",
                "#F33BFD",
                "#62E5F3",
            ];

            y[0].forEach(target => {
                this.chartTreatedData.datasets.push({
                    yAxisID: "y-axis-0",
                    label: "Combined",
                    pointBorderWidth: 0,
                    pointStyle: "cross",
                    spanGaps: true,
                    pointBorderColor: colors[0],
                    pointBackgroundColor: "transparent",
                    backgroundColor: "transparent",
                    borderColor: colors[0],
                    borderWidth: 2,
                    data: target,
                });
            });
            this.chartTreatedData.datasets.push({
                yAxisID: "y-axis-0",
                label: "Baseline combined",
                pointRadius: 5,
                pointBorderWidth: 2,
                pointStyle: "circle",
                backgroundColor: "transparent",
                borderColor: colors[0],
                data: [baseline[0]],
            });

            this.goal.parameters.targets.forEach((t, i) => {
                y[i + 1].forEach(target => {
                    this.chartTreatedData.datasets.push({
                        yAxisID: "y-axis-0",
                        label: t,
                        pointBorderWidth: 0,
                        pointStyle: "cross",
                        spanGaps: true,
                        pointBorderColor: colors[i + 1],
                        pointBackgroundColor: "transparent",
                        backgroundColor: "transparent",
                        borderColor: colors[i + 1],
                        borderWidth: 2,
                        data: target,
                    });
                });
                this.chartTreatedData.datasets.push({
                    yAxisID: "y-axis-0",
                    label: `Baseline ${t}`,
                    pointBorderWidth: 0,
                    pointStyle: "cross",
                    pointBorderColor: colors[i + 1],
                    pointBackgroundColor: "transparent",
                    backgroundColor: "transparent",
                    borderColor: colors[i + 1],
                    borderWidth: 1,
                    data: [baseline[i + 1]],
                });
            });
        },

        generateDurationSequences(y, y2, baseline, baseline2) {
            if (this.goal && this.goal.domain) {
                y.forEach(dataset => {
                    this.chartTreatedData.datasets.push({
                        label: "Duration",
                        yAxisID: "y-axis-0",
                        pointBorderWidth: 0,
                        pointStyle: "rect",
                        spanGaps: true,
                        pointBorderColor: "#D43672",
                        pointBackgroundColor: "#D43672",
                        backgroundColor: "transparent",
                        borderColor: this.goal.domain.color,
                        data: dataset,
                    });
                });
            }
            y2.forEach(dataset => {
                if (this.goal && this.goal.domain) {
                    this.chartTreatedData.datasets.push({
                        label: "Times",
                        yAxisID: "y-axis-1",
                        pointBorderWidth: 0,
                        pointStyle: "cross",
                        spanGaps: true,
                        pointBorderColor: "#D43672",
                        pointBackgroundColor: "#D43672",
                        backgroundColor: "transparent",
                        borderDash: [10, 5],
                        borderColor: this.goal.domain.color,
                        data: dataset.map(day => (day ? day[0] : null)),
                    });
                }
            });
            const iconset = _.get(this.customization, "default_options.iconset", 0);
            let colors = [];
            switch (iconset) {
                case 0:
                    colors = ["#58dd55", "#42e6c7", "#f6d70c", "#f99808", "#d53e3f"];
                    break;
                case 1:
                    colors = ["#f6d70c", "transparent", "#f99808", "transparent", "#d53e3f"];
            }

            for (let i = 1; i <= 5; ) {
                let label = "";
                switch (iconset) {
                    case 0:
                        label = `Intensity ${i}`;
                        break;
                    case 1:
                        switch (i) {
                            case 1:
                                label = "Min. intensity";
                                break;
                            case 3:
                                label = "Med. intensity";
                                break;
                            case 5:
                                label = "Max. intensity";
                        }
                }
                y2.forEach(dataset => {
                    this.chartTreatedData.datasets.push({
                        label: label,
                        yAxisID: "y-axis-1",
                        pointBorderWidth: 0,
                        pointStyle: "cross",
                        spanGaps: true,
                        backgroundColor: "transparent",
                        borderColor: colors[i - 1],
                        data: dataset.map(day => (day ? day[i] : null)),
                    });
                });
                switch (iconset) {
                    case 0:
                        i = i + 1;
                        break;
                    case 1:
                        i = i + 2;
                }
            }

            this.chartTreatedData.datasets.push(
                {
                    yAxisID: "y-axis-0",
                    label: "Baseline duration",
                    pointRadius: 5,
                    pointBorderWidth: 2,
                    pointStyle: "circle",
                    backgroundColor: "transparent",
                    borderColor: "#8f8f8f",
                    data: [baseline[0]],
                },
                {
                    yAxisID: "y-axis-1",
                    label: "Baseline times",
                    pointRadius: 5,
                    pointBorderWidth: 2,
                    pointStyle: "cross",
                    backgroundColor: "transparent",
                    borderColor: "#8f8f8f",
                    data: [baseline2[0][0]],
                }
            );
        },

        populateBarChart(x, y0) {
            if (y0.length === 1 && Array.isArray(y0[0])) {
                y0 = y0[0]; //This is wrapped in an array because of Phase Change Lines
            }
            if (this.goal && this.goal.title && this.goal.domain) {
                this.chartTreatedData = {
                    yAxisID: "y-axis-0",
                    labels: x,
                    datasets: [
                        {
                            label: this.goal.title,
                            pointStyle: "rect",
                            backgroundColor: this.goal.domain.color,
                            data: y0,
                        },
                    ],
                };
            }
        },
    },
};
</script>

<style scoped lang="scss">
.widget {
    background-color: #fff;
}
</style>
